import React, { useEffect, useState } from "react";
import Papa from "papaparse";
import { csvText } from "./Members Data/Members Data.js";
import PublicHeader from '../Header/PublicHeader';
import Footer from '../Footer';
import { motion, AnimatePresence } from 'framer-motion'

const MemberDirectory = () => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchField, setSearchField] = useState("MembershipID");
  const itemsPerPage = 6;

  useEffect(() => {
    Papa.parse(csvText, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const formattedData = results.data.map((item) => ({
          Name: item["Your Full Name"],
          City: item["City"],
          State: item["State"],
          Photo: item["Your Professional Photograph (currently URLs)"],
          CareerCounsellingExperience: item["Total Professional Experience (In years)"],
          Expertise: item["Your Core Area of Expertise"],
          MembershipID: item["Membership ID"],
          MembershipValidity: item["Valid up to"]
        }));

        const extractNumber = (id) => {
          if (!id) return 0;
          const match = id.match(/-(\d+)$/); // get number after last dash
          return match ? parseInt(match[1], 10) : 0;
        };

        const sortedData = formattedData.sort((a, b) => {
          return extractNumber(a.MembershipID) - extractNumber(b.MembershipID);
        });

        setData(sortedData);
      },
    });
  }, []);

  const filteredData = data.filter((item) => {
    const term = searchTerm.toLowerCase();
    const fieldValue = item[searchField]?.toLowerCase() || "";
    return fieldValue.includes(term);
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <>
    <PublicHeader/>
    <div
      className="section-header"
         
    >
      <div className="header-content px-5">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-left"
        >
          <h1 className="text-center display-4 fw-bold mb-2 text-white">Members' Directory</h1>

        </motion.div>
      </div>
      <div className="overlay"></div>
    </div>
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex flex-col md:flex-row gap-2 mb-6">
        <div className="relative w-full md:w-48">
          <select
            value={searchField}
            onChange={(e) => setSearchField(e.target.value)}
            className="
              w-full
              p-2
              pr-10
              border border-gray-300
              rounded-md
              bg-white
              text-gray-700
              font-medium
              shadow-md
              appearance-none
              focus:outline-none
              focus:ring-2 focus:ring-[#0078BF]
              focus:border-transparent
              hover:border-[#0078BF]
              transition-all duration-200
              cursor-pointer
            "
          >
            <option value="Name">Name</option>
            <option value="City">City</option>
            <option value="State">State</option>
            <option value="MembershipID">Membership ID</option>
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
            <svg
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.25 8.29a.75.75 0 01-.02-1.08z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
        <input
          type="text"
          placeholder={`Search by ${searchField === "MembershipID" ? "Membership ID" : searchField}...`}
          className="w-full md:flex-1 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0078BF] focus:border-transparent transition"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentItems.map((member, index) => (
          <div
            key={index}
            className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition duration-300"
          >
            {/* Desktop view block */}
            <div className="hidden md:block">
              <div className="flex flex-col items-center p-6 text-center">
                {member.Photo && (
                  <img
                    src={member.Photo}
                    alt={member.Name}
                    className="w-24 h-24 rounded-full object-cover mb-4 border border-gray-300"
                  />
                )}
                <h3 className="text-xl font-semibold text-gray-800 mb-1">{member.Name}</h3>
                <p className="text-sm text-gray-500 mb-2">
                  {member.City}, {member.State}
                </p>
              </div>
            </div>
          
            {/* Mobile view block */}
            <div className="block md:hidden">
              <div className="flex flex-row items-start p-4 text-left">
                {member.Photo && (
                  <img
                    src={member.Photo}
                    alt={member.Name}
                    className="w-24 h-24 rounded-full object-cover bg-gray-200 border border-gray-300 mr-3"
                  />
                )}
                <div className="flex flex-col self-center">
                  <h3 className="text-base font-semibold text-gray-800">{member.Name}</h3>
                  <p className="text-xs text-gray-500">
                    {member.City}, {member.State}
                  </p>
                </div>
              </div>
            </div>

            {/* Common details block */}
            <div className="text-xs md:text-sm text-gray-700 text-left w-full px-4 md:px-6 pb-4 overflow-y-auto max-h-32">
              <p><strong>Membership ID:</strong> {member.MembershipID}</p>
              <p><strong>Expertise:</strong> {member.Expertise}</p>
              <p><strong>Career Counselling Experience:</strong> {member.CareerCounsellingExperience} yrs</p>
              <p><strong>Membership Validity:</strong> {member.MembershipValidity}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center mt-8 flex-wrap gap-4">
        <div className="text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
          >
            First
          </button>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
          >
            Prev
          </button>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
          >
            Next
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm border rounded-md disabled:opacity-50"
          >
            Last
          </button>
        </div>
      </div>
    </div>
    <Footer />
    </>
  );
};

export default MemberDirectory;
